"use client";

import { Button } from "@decibeltrade/web-core/components";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { css } from "styled-system/css";
import { stack } from "styled-system/patterns";

import { env } from "@/env";
import { useLatestAppVersion } from "@/hooks/use-is-online";

import { FancyNotificationCollapsible } from "./fancy-notification";

const ONE_SECOND = 1000;
const ONE_MIN = 60 * ONE_SECOND;

const FORCE_RELOAD_TIME = ONE_MIN;
const SNOOZE_TIME = 5 * ONE_MIN;

export interface AppUpdateNotificationProps {
  className?: string;
}

export function AppUpdateNotification({ className }: AppUpdateNotificationProps) {
  const t = useTranslations("appUpdateNotification");
  const [hasUpdate, setHasUpdate] = useState(false);
  const [timeToReload, setTimeToReload] = useState(FORCE_RELOAD_TIME);

  const showUpdateNotification = timeToReload < FORCE_RELOAD_TIME;

  const latestVersion = useLatestAppVersion();

  if (!hasUpdate && latestVersion !== null && latestVersion !== env.NEXT_PUBLIC_APP_VERSION) {
    setHasUpdate(true);
  }

  // Countdown to reload once the update is detected
  useEffect(() => {
    if (!hasUpdate) return;

    const countdown = () => {
      setTimeToReload((prev) => {
        const next = prev - ONE_SECOND;
        if (next <= 0) {
          window.location.reload();
          return 0;
        }
        return next;
      });
    };

    const interval = window.setInterval(countdown, ONE_SECOND);

    return () => clearInterval(interval);
  }, [hasUpdate]);

  return (
    <FancyNotificationCollapsible
      canMinimize
      visible={showUpdateNotification}
      title={t("title")}
      className={className}
    >
      <div className={stack({ px: "12", py: "8", gap: "8", w: "full" })}>
        <div
          className={css({
            textStyle: "highlight.sm",
            color: "text.tertiary",
            textAlign: "center",
          })}
        >
          {t("description", { seconds: timeToReload / ONE_SECOND })}
        </div>
        <div className={stack({ py: "12", gap: "8" })}>
          <Button variant="brand" fullWidth onClick={() => window.location.reload()}>
            {t("reloadButton")}
          </Button>
          <Button
            variant="secondary"
            fullWidth
            onClick={() => setTimeToReload((prev) => prev + SNOOZE_TIME)}
          >
            {t("snoozeButton", { minutes: SNOOZE_TIME / ONE_MIN })}
          </Button>
        </div>
      </div>
    </FancyNotificationCollapsible>
  );
}
